import { replaceHtmlHrefs } from '@realadvisor/transform/html-href';

import { isSpecialLink } from '@modules/_shared/components/linkUtils';

import { escapeAttr } from './draftConvertCore';
import type { EmailEditorSuggestionMap } from './types';

export const UTM_PARAMS =
  'utm_source=crm_email&utm_medium=email&utm_campaign=custom_link';

const ENCODED_UTM_PARAMS = UTM_PARAMS.replaceAll('&', '&amp;');
const TRACKING_ENTRIES = UTM_PARAMS.split('&');
export const isDynamicLink = (url: string): boolean =>
  url.startsWith('#') && url.length > 1;

const isSafeResolvedLink = (url: string): boolean =>
  /^(?:https?|ftp|mailto|tel):/i.test(url);

export const normalizeLegacyDynamicLink = (url: string): string => {
  for (const prefix of [`?${UTM_PARAMS}#`, `?${ENCODED_UTM_PARAMS}#`]) {
    if (url.startsWith(prefix)) {
      return `#${url.slice(prefix.length)}`;
    }
  }
  return url;
};

const splitFragment = (url: string): [string, string] => {
  const hash = url.indexOf('#');
  return hash === -1 ? [url, ''] : [url.slice(0, hash), url.slice(hash)];
};

export const appendUTM = (url: string): string => {
  const normalized = normalizeLegacyDynamicLink(url);
  if (isDynamicLink(normalized) || isSpecialLink(normalized)) {
    return normalized;
  }

  const decodedUrl = normalized.replaceAll('&amp;', '&');
  const [base, fragment] = splitFragment(decodedUrl);
  const params = new URLSearchParams(base.slice(base.indexOf('?') + 1));
  if (
    params.get('utm_source') === 'crm_email' &&
    params.get('utm_medium') === 'email' &&
    params.get('utm_campaign') === 'custom_link'
  ) {
    return decodedUrl;
  }

  const separator = base.includes('?')
    ? base.endsWith('?') || base.endsWith('&')
      ? ''
      : '&'
    : '?';
  return base + separator + UTM_PARAMS + fragment;
};

export const removeUTM = (url: string): string => {
  const normalized = normalizeLegacyDynamicLink(url);
  const [base, fragment] = splitFragment(normalized);
  const queryIndex = base.indexOf('?');
  if (queryIndex === -1) {
    return normalized;
  }

  const ampersand = base.includes('&amp;') ? '&amp;' : '&';
  const query = base.slice(queryIndex + 1);
  const remaining = query
    .split(ampersand)
    .filter(part => !TRACKING_ENTRIES.includes(part))
    .join(ampersand);
  return (
    base.slice(0, queryIndex) +
    (remaining === '' ? '' : `?${remaining}`) +
    fragment
  );
};

export const normalizeDynamicLinksInHtml = (html: string): string =>
  replaceHtmlHrefs(html, ({ match, prefix, quote, href }) => {
    const normalized = normalizeLegacyDynamicLink(href);
    return normalized === href
      ? match
      : `${prefix}${quote}${normalized}${quote}`;
  });

export const resolveDynamicLinksInHtml = (
  html: string,
  suggestions: EmailEditorSuggestionMap,
): string =>
  replaceHtmlHrefs(html, ({ match, prefix, href }) => {
    const normalized = normalizeLegacyDynamicLink(href);
    if (!isDynamicLink(normalized)) {
      return match;
    }

    const suggestion = suggestions[normalized];
    const value =
      typeof suggestion === 'string' ? suggestion : suggestion?.value;
    let resolved = normalized;
    if (value != null && value.trim() !== '' && isSafeResolvedLink(value)) {
      resolved = /^https?:\/\//i.test(value) ? appendUTM(value) : value;
    }
    return `${prefix}"${escapeAttr(resolved)}"`;
  });
